import { Component, OnInit } from '@angular/core';
import {
  ColDef,
  ColumnApi,
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  ValueSetterParams
} from 'ag-grid-community';
import { HttpService } from 'src/app/services/http.service';
import {environment} from '../../../environments/environment';
import {ConfirmationCellComponent} from '../../grid/components/confirmation-cell/confirmation-cell.component';
import {UserManagementActionsComponent} from '../cell/user-management-actions/user-management-actions.component';
import { deepmerge } from 'deepmerge-ts';
import {ToastService} from '../../services/toast.service';
import {
  logExperimentalWarnings
} from '@angular-devkit/build-angular/src/builders/browser-esbuild/experimental-warnings';
import {Observable} from 'rxjs';
import {shareReplay} from 'rxjs/operators';
import {AuthService} from '../../services/auth.service';
import {HistoryDialogComponent} from '../../grid/dialogs/history-dialog/history-dialog.component';

interface IUserAccess {
  level?: number;
  group?: string;
}

interface IUser {
  _id?: string;
  new?: boolean;
  name?: string;
  email?: string;
  access?: IUserAccess;
  avatar?: string;
  password?: string;
}

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit {
  public user$: Observable<any>;
  private gridApi?: GridApi;
  private columnApi?: ColumnApi;

  public defaultColDef: ColDef = {
    flex: 1,
    sortable: true,
    editable: true,
    filter: true,
    resizable: true,
    filterParams: {
      buttons: ['reset'],
    },
    cellClass: (params: any) => {
      if (!params.data._id.includes('new-')) {
        // @ts-ignore
        const changed = JSON.stringify(this.originalUsers.find(user => user._id === params?.data?._id)[params?.column?.colId]) !== JSON.stringify(params.value);
        const disabled = params.data._id === this.authService.currentUserValue._id;
        if (changed && !disabled) {
          return 'user-cell-changed';
        } else if (disabled) {
          return 'user-cell-disabled';
        } else {
          return '';
        }
      } else {
        return 'new-user-cell';
      }
    }
  }
  public columnDefs: ColDef[] = [
    {
      field: '_id',
      headerName: 'ID',
      hide: true,
      editable: false,
    },
    {
      field: 'name',
      headerName: 'Name',
      editable: true,
      cellRenderer: (data: any) => {
        if (data.value) {
          return data.value;
        } else {
          return 'User Name*';
        }
      }
    },
    {
      field: 'email',
      headerName: 'Email',
      editable: true,
      cellRenderer: (data: any) => {
        if (data.value) {
          return data.value;
        } else {
          return 'User Email*';
        }
      }
    },
    {
      field: 'password',
      headerName: 'Password',
      editable: true,
      cellRenderer: (data: any) => {
        console.log('-+_+_', data);
        if (data.data.new && !data?.value) {
          return 'User Password*';
        } else {
          return '********';
        }
      }
    },
    {
      field: 'access',
      headerName: 'Role',
      editable: true,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorPopup: true,
      cellRenderer: (data: any) => {
        return data.value.group;
      },
      cellEditorParams: {
        values: [
          {
            level: 1,
            group: 'admin'
          },
          {
            level: 2,
            group: 'manager'
          },
          {
            level: 3,
            group: 'user'
          },
          {
            level: 4,
            group: 'deactivated'
          }
        ],
        cellRenderer: (data: any) => {
          return data.value.group;
        }
      }
    },
    {
      field: 'avatar',
      headerName: 'Avatar',
      editable: true,
      cellRenderer: (data: any) => {
        if (data.value) {
          return data.value;
        } else {
          return 'User Avatar';
        }
      }
    },
    // {
    //   pinned: 'right',
    //   maxWidth: 79,
    //   cellRenderer: UserManagementActionsComponent,
    // }
  ];

  private originalUsers: IUser[] = [];
  public rowData: Array<IUser> = [];

  constructor(
    private httpService: HttpService,
    private toastService: ToastService,
    private authService: AuthService,
  ) {
    this.user$ = this.authService.getCurrentUser().pipe(
      shareReplay()
    );
  }

  ngOnInit(): void {
    this.getUsers();
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
  }

  private getUsers(): void {
    this.httpService.get(environment.API_PATH + '/user/getUsers').subscribe((res: Array<IUser>) => {
      this.rowData = structuredClone(res);
      this.originalUsers = structuredClone(res);
    });
  }
  public submitData: any = [];
  cellValueChanged($event: any): void {
    const exist = this.submitData.find((x: any) => x._id === $event.data._id);
    if (exist) {
      exist[$event.column.colId] = $event.value;
    } else {
      this.submitData.push({
        _id: $event.data._id,
        [$event.column.colId]: $event.value
      });
    }
    console.log('this.originalUsers', this.originalUsers);
  }

  private checkNewUserFields(user: IUser): boolean {
    const name = user.name && user.name.length > 0;
    const email = user.email && user.email.length > 0;
    const password = user.password && user.password.length > 0;
    return !!(name && email && password);
  }

  onSubmitChanges(): void {
    console.log('this.submitData', this.submitData);
    let error = false;
    for (const data of this.submitData) {
      if (data.new) {
        if (!this.checkNewUserFields(data)) {
          error = true;
        }
      }
    }
    if (error) {
      this.toastService.error('Please fill all required fields', '', {
        timeOut: 3000,
      });
    } else {
      this.httpService.post(environment.API_PATH + '/user/management', this.submitData).subscribe(res => {
        this.submitData = [];
        this.toastService.success('Successfully updated');
        this.getUsers();
      });
    }
  }

  onCancelChanges(): void {
    this.submitData = [];
    this.rowData = structuredClone(this.originalUsers);
    this.authService.refreshToken();
  }

  addNewUser(): void {
    const newUser: IUser = {
      access: {
        level: 3,
        group: 'user'
      },
      new: true,
      _id: `new-${String(Math.floor(Math.random() * 7000000000))}`
    };

    this.submitData.push(newUser);
    this.gridApi?.applyTransaction({
      add: [newUser]
    })
  }

  getContextMenuItems = (params: GetContextMenuItemsParams) => {
    return [];
  }
}
